import React, { useEffect, useState } from 'react';
import Container from '@/components/ui/container';
import InputUi from '@/components/ui/inputui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import Layout from 'src/layout';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { getFilteredCoupons, getCouponCount } from '../../lib/api/couponsApi';
import { useNavigate } from 'react-router-dom';
import {
    RiSearchLine,
    RiRefreshLine,
    RiAddLine,
    RiCouponLine,
    RiUserLine,
    RiPercentLine,
    RiCloseLine,
    RiEditLine,
    RiEyeLine
} from 'react-icons/ri';

const ListCoupons = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        couponCode: '',
        assignedUser: '',
        discountType: '',
        discountValue: '',
    });

    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;

    const totalPages = Math.ceil(totalCount / limit) || 1;

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchCoupons = async (overrideFilters = filters, pageToFetch = page) => {
        try {
            setLoading(true);
            setRefreshing(true);
            const data = await getFilteredCoupons({ ...overrideFilters, page: pageToFetch, limit });

            if (data.success) {
                setCoupons(data.data || []);
            } else {
                setCoupons([]);
            }
        } catch (error) {
            console.error('Error fetching coupons:', error);
            setCoupons([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchCount = async (overrideFilters = filters) => {
        try {
            const data = await getCouponCount(overrideFilters);
            if (data.success) {
                setTotalCount(data.total || 0);
            } else {
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching count:', error);
            setTotalCount(0);
        }
    };

    const handleSearch = async () => {
        setPage(1);
        await fetchCount(filters);
        await fetchCoupons(filters, 1);
    };

    const handleRefresh = async () => {
        await fetchCount(filters);
        await fetchCoupons(filters, page);
    };

    const handleClearFilters = () => {
        const cleared = {
            couponCode: '',
            assignedUser: '',
            discountType: '',
            discountValue: '',
        };
        setFilters(cleared);
        setPage(1);
        fetchCount(cleared);
        fetchCoupons(cleared, 1);
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || loading) return;
        setPage(newPage);
        fetchCoupons(filters, newPage);
    };

    useEffect(() => {
        fetchCount(filters);
        fetchCoupons(filters, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Layout active={'admin-coupons-list'} title={'All Coupons - Super Admin'}>
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header Section */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiCouponLine className="text-orange-600" /> Coupon Management
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Create, monitor, and manage discount promo codes and user vouchers.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                variant="outline"
                                className="flex items-center gap-2 bg-white hover:bg-slate-100 border-slate-200 text-slate-700 shadow-sm"
                            >
                                <RiRefreshLine className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Button
                                onClick={() => navigate('/coupons/add')}
                                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white shadow-sm font-medium"
                            >
                                <RiAddLine className="w-4 h-4" />
                                Add Coupon
                            </Button>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Coupons</p>
                                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{totalCount}</p>
                                </div>
                                <div className="p-3 bg-orange-50 rounded-xl">
                                    <RiCouponLine className="w-6 h-6 text-orange-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Percentage Off</p>
                                    <p className="text-2xl font-extrabold text-emerald-600 mt-1">
                                        {coupons.filter(c => c.discountType === 'percentage').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-xl">
                                    <RiPercentLine className="w-6 h-6 text-emerald-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Flat Discount</p>
                                    <p className="text-2xl font-extrabold text-blue-600 mt-1">
                                        {coupons.filter(c => c.discountType === 'flat').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-xl">
                                    <RiCouponLine className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned to User</p>
                                    <p className="text-2xl font-extrabold text-purple-600 mt-1">
                                        {coupons.filter(c => c.assignedUser).length}
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-xl">
                                    <RiUserLine className="w-6 h-6 text-purple-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="relative">
                                <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    value={filters.couponCode}
                                    datafunction={(val) => handleFilterChange('couponCode', val.target.value)}
                                    placeholder="Search by Coupon Code"
                                    className="pl-9"
                                />
                            </div>
                            <div className="relative">
                                <RiUserLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    value={filters.assignedUser}
                                    datafunction={(val) => handleFilterChange('assignedUser', val.target.value)}
                                    placeholder="Search by User"
                                    className="pl-9"
                                />
                            </div>
                            <div className="flex flex-col">
                                <select
                                    className="w-full h-[42px] border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                    value={filters.discountType}
                                    onChange={(e) => handleFilterChange('discountType', e.target.value)}
                                >
                                    <option value="">All Discount Types</option>
                                    <option value="percentage">Percentage</option>
                                    <option value="flat">Flat Discount</option>
                                </select>
                            </div>
                            <div className="relative">
                                <RiPercentLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    type="number"
                                    value={filters.discountValue}
                                    datafunction={(val) => handleFilterChange('discountValue', val.target.value)}
                                    placeholder="Discount Value"
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleSearch}
                                disabled={loading}
                                className="px-5 bg-orange-600 hover:bg-orange-700 text-white font-medium shadow-sm"
                            >
                                {loading ? 'Searching...' : 'Search Coupons'}
                            </Button>
                            <Button
                                onClick={handleClearFilters}
                                variant="outline"
                                className="px-4 text-slate-600 border-slate-200 hover:bg-slate-50"
                            >
                                <RiCloseLine className="w-4 h-4 mr-1.5" />
                                Clear Filters
                            </Button>
                        </div>
                    </div>

                    {/* Main Table */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">Coupons</h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                                Showing {coupons.length} of {totalCount} total
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader>
                                    <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Coupon ID
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Code
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Assigned User
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Times Used
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Global Limit
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Per-User Limit
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Min Order (₹)
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Discount Value
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Type
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <TableSkeleton columns={10} rows={5} />
                                    ) : coupons.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <RiCouponLine className="w-12 h-12 text-slate-300 mb-3" />
                                                    <p className="text-slate-700 font-semibold text-base">No coupons found</p>
                                                    <p className="text-slate-400 text-xs mt-1">Try adjusting search parameters.</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        coupons.map((coupon, idx) => (
                                            <TableRow
                                                key={coupon.couponID || idx}
                                                className="hover:bg-slate-50/80 transition-colors"
                                            >
                                                <TableCell className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-xs">
                                                            {String(coupon.couponID || '').slice(-3) || 'CPN'}
                                                        </div>
                                                        <span className="text-sm font-mono font-medium text-slate-900">
                                                            {coupon.couponID || 'N/A'}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-bold text-slate-900 bg-orange-50 text-orange-800 border border-orange-200 px-2.5 py-1 rounded-md">
                                                        {coupon.couponCode || 'N/A'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap">
                                                    {coupon.assignedUser ? (
                                                        <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
                                                            <RiUserLine className="text-purple-600 w-4 h-4" />
                                                            {coupon.assignedUser}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-slate-400 font-medium">All Users</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center font-bold text-slate-800">
                                                    {coupon.couponUsage || 0}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                                                    {coupon.usageLimit ?? '∞'}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                                                    {coupon.maxUsagePerUser ?? '∞'}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                                                    {coupon.minOrderValue != null ? `₹${coupon.minOrderValue}` : '-'}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center text-base font-extrabold text-slate-900">
                                                    {coupon.discountValue}
                                                    {coupon.discountType === 'percentage' ? '%' : '₹'}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                                        coupon.discountType === 'percentage'
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                                                    }`}>
                                                        {coupon.discountType === 'percentage' ? '% Percent' : '₹ Flat'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => navigate(`/coupons/edit/${coupon.couponID}`)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                                        >
                                                            <RiEditLine className="w-3.5 h-3.5" />
                                                            Edit
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                                <span className="text-xs text-slate-500">
                                    Page <strong className="text-slate-800">{page}</strong> of <strong className="text-slate-800">{totalPages}</strong>
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1 || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default ListCoupons;
